import { useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useAppSelector } from '@hooks/store';
import { ROUTES } from '@router/routes';
import { selectProjectTabs } from '@store/slices/projectSlice';
import { useModelFilterActions } from '@store/zustand/model-filters';

const PROJECT_WORKSPACE_PATTERN = `${ROUTES.PROJECT.HOME}/*`;

/**
 * Where a location sits in the project workspace: the project id, or `null`
 * outside `/projects/:id`, and whether it is the what-if scenario side of that
 * project. Scenario mode is read the way `useProjectId` reads it — the
 * `scenario` path segment, or the `?scenario=true` the questionnaire routes
 * carry — but by whole segment rather than substring, so the Scenarios list
 * page stays on the main model's side.
 */
const resolveWorkspace = (pathname: string, search: string) => {
  const match = matchPath({ path: PROJECT_WORKSPACE_PATTERN }, pathname);
  const projectId = match?.params.id;

  if (!projectId) {
    return { projectId: null, isScenario: false };
  }

  const rest = match?.params['*'] ?? '';
  const isScenario =
    rest === 'scenario' ||
    rest.startsWith('scenario/') ||
    new URLSearchParams(search).get('scenario') === 'true';

  return { projectId, isScenario };
};

/**
 * Keys the model-filter store to the project or scenario the route is in, and
 * resets it on the way out.
 *
 * The filters provider wraps the whole authenticated app and never unmounts,
 * so leaving a project clears nothing on its own. AC7.d.iv wants a clean slate
 * on return from the Projects list, My Profile, User support, the API docs or
 * another project, so a route outside `/projects/:id`, or inside a different
 * project, resets the store, parked buckets included. Within one project the
 * route only moves the scope key: Model, Dashboard and Insights share a bucket
 * (AC7.d.ii), and a what-if scenario gets its own, keyed by the scenario id,
 * so it opens unfiltered and the main model's selection comes back on return
 * (AC7.d.i). Unmounting — log-out, or a failed token refresh — resets as well,
 * so no selection outlives the session.
 */
export const useResetFiltersOnLeaveProject = () => {
  const { pathname, search } = useLocation();
  const projectsTabs = useAppSelector(selectProjectTabs);
  const { reset, setScope } = useModelFilterActions();
  const previousProjectId = useRef<string | null>(null);

  const { projectId, isScenario } = resolveWorkspace(pathname, search);
  const scenarioId =
    projectId && isScenario ? projectsTabs[projectId]?.currentId ?? null : null;
  const scopeKey = isScenario
    ? scenarioId ?? `${projectId}/scenario`
    : projectId;

  useEffect(() => {
    const hasLeftProject =
      previousProjectId.current !== null &&
      previousProjectId.current !== projectId;

    if (hasLeftProject) {
      reset();
    }

    if (scopeKey) {
      setScope(scopeKey);
    }

    previousProjectId.current = projectId;
  }, [projectId, scopeKey]);

  useEffect(() => () => reset(), []);
};
